import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const typeColors = {
  generic_pharma: '#6366f1', innovator_pharma: '#0ea5e9', cro: '#10b981',
  biotech: '#f59e0b', research_institution: '#8b5cf6', api_manufacturer: '#ec4899', other: '#94a3b8',
};
const typeLbl = t => (t || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtCr = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${fmt(v)}`;
};
const safeAdd = (...vals) => vals.reduce((s, v) => s + (Number(v) || 0), 0);

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 22px', minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: color || '#1e293b', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function PartnerCard({ p }) {
  const { isAdmin } = useAuth();
  const color = typeColors[p.type] || '#94a3b8';
  const initials = p.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
  const totalOrders = Number(p.invoice_count) + Number(p.proforma_count) + Number(p.po_count);

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, transition: 'box-shadow 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: `${color}18`, border: `2px solid ${color}33`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem' }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: `${color}15`, color, border: `1px solid ${color}30` }}>
              {typeLbl(p.type)}
            </span>
            {p.country && <span style={{ fontSize: '0.68rem', color: '#94a3b8', padding: '2px 6px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>{p.country}</span>}
          </div>
        </div>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', whiteSpace: 'nowrap' }}>PARTNER</span>
      </div>

      {/* Revenue highlight */}
      <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total Business</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#15803d' }}>{fmtCr(safeAdd(p.invoice_total, p.po_total))}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 7, padding: '6px 10px' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 2 }}>Invoice Revenue</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#15803d' }}>{fmtCr(p.invoice_total)}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 7, padding: '6px 10px' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', marginBottom: 2 }}>PO Value</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0ea5e9' }}>{fmtCr(p.po_total)}</div>
          </div>
        </div>
      </div>

      {/* Order breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {[
          { label: 'Invoices', value: p.invoice_count, color: '#6366f1', bg: '#eef2ff' },
          { label: 'Proformas', value: p.proforma_count, color: '#f97316', bg: '#fff7ed' },
          { label: 'POs', value: p.po_count, color: '#0ea5e9', bg: '#f0f9ff' },
        ].map(({ label, value, color: c, bg }) => (
          <div key={label} style={{ background: bg, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: c }}>{value}</div>
            <div style={{ fontSize: '0.65rem', fontWeight: 600, color: c, opacity: 0.8 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Projects */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <svg width="15" height="15" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
        </svg>
        <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>{p.project_count}</span>
        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>project{p.project_count !== 1 ? 's' : ''}</span>
        {Number(p.active_projects) > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: '#dcfce7', color: '#15803d' }}>
            {p.active_projects} active
          </span>
        )}
        {Number(p.completed_projects) > 0 && (
          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: '#e0f2fe', color: '#0369a1' }}>
            {p.completed_projects} done
          </span>
        )}
      </div>

      {/* Contact */}
      {(p.email || (isAdmin && p.assigned_to_name)) && (
        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {p.email && <span style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email}</span>}
          {isAdmin && p.assigned_to_name && (
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6366f1', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
              BD: {p.assigned_to_name}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function Partners() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('grid'); // 'grid' | 'table'

  useEffect(() => {
    setLoading(true);
    api.get('/clients/partners')
      .then(r => setData(r.data))
      .catch(() => setData({ partners: [], summary: {} }))
      .finally(() => setLoading(false));
  }, []);

  const partners = (data?.partners || []).filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.country || '').toLowerCase().includes(search.toLowerCase())
  );

  const summary = data?.summary || {};

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Partners</h1>
          <p className="page-subtitle">Business overview for flagged partner clients</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${view === 'grid' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('grid')} style={{ padding: '6px 14px' }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            Cards
          </button>
          <button className={`btn ${view === 'table' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('table')} style={{ padding: '6px 14px' }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            Table
          </button>
        </div>
      </div>

      {/* Summary stats */}
      {!loading && data && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
          <StatCard label="Total Partners" value={summary.total_partners || 0} color="#6366f1" />
          <StatCard label="Total Business"
            value={fmtCr(safeAdd(summary.total_invoice_revenue, summary.total_po_value))}
            sub={`Invoice ${fmtCr(summary.total_invoice_revenue)} + PO ${fmtCr(summary.total_po_value)}`}
            color="#15803d" />
          <StatCard label="Invoice Revenue" value={fmtCr(summary.total_invoice_revenue)} sub={`${fmt(summary.total_invoices)} invoices`} color="#6366f1" />
          <StatCard label="PO Value" value={fmtCr(summary.total_po_value)} sub={`${fmt(summary.total_pos)} POs`} color="#0ea5e9" />
          <StatCard label="Proforma Invoices" value={fmt(summary.total_proformas)} color="#f97316" />
          <StatCard label="Total Projects" value={fmt(summary.total_projects)} color="#8b5cf6" />
        </div>
      )}

      {/* Search */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: '1 1 260px' }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input className="form-control" placeholder="Search partner name or country…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <span style={{ fontSize: '0.82rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
              {partners.length} partner{partners.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      )}

      {!loading && partners.length === 0 && (
        <div className="card">
          <div className="empty-state" style={{ padding: 60 }}>
            <svg width="48" height="48" fill="none" stroke="#cbd5e1" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom: 16 }}>
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
            </svg>
            <p style={{ color: '#94a3b8', fontWeight: 600 }}>No partners yet.</p>
            <p style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>Go to Clients and check "Mark as Partner" on the clients you work with closely.</p>
          </div>
        </div>
      )}

      {/* Grid view */}
      {!loading && partners.length > 0 && view === 'grid' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {partners.map(p => <PartnerCard key={p.id} p={p} />)}
        </div>
      )}

      {/* Table view */}
      {!loading && partners.length > 0 && view === 'table' && (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Type</th>
                  <th>Country</th>
                  <th>Inv#</th>
                  <th>PI#</th>
                  <th>PO#</th>
                  <th>Invoice Revenue</th>
                  <th>PO Value</th>
                  <th>Total</th>
                  <th>Projects</th>
                  <th>Active</th>
                  {isAdmin && <th>BD Owner</th>}
                </tr>
              </thead>
              <tbody>
                {partners.map(p => {
                  const color = typeColors[p.type] || '#94a3b8';
                  const initials = p.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: `${color}15`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem', border: `1.5px solid ${color}25` }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.name}</div>
                            {p.email && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{p.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td><span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: `${color}12`, color, border: `1px solid ${color}25` }}>{typeLbl(p.type)}</span></td>
                      <td style={{ fontSize: '0.82rem', color: '#64748b' }}>{p.country || '—'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#6366f1' }}>{p.invoice_count}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#f97316' }}>{p.proforma_count}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#0ea5e9' }}>{p.po_count}</td>
                      <td style={{ fontWeight: 700, color: '#15803d' }}>{fmtCr(p.invoice_total)}</td>
                      <td style={{ fontWeight: 700, color: '#0ea5e9' }}>{fmtCr(p.po_total)}</td>
                      <td style={{ fontWeight: 800, color: '#1e293b' }}>{fmtCr(safeAdd(p.invoice_total, p.po_total))}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{p.project_count}</td>
                      <td style={{ textAlign: 'center' }}>
                        {Number(p.active_projects) > 0
                          ? <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: '#dcfce7', color: '#15803d' }}>{p.active_projects}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>
                        }
                      </td>
                      {isAdmin && <td style={{ fontSize: '0.82rem', color: '#6366f1', fontWeight: 600 }}>{p.assigned_to_name || '—'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
